import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { ControllerStateAndHelpers } from 'downshift';
import classNames from 'clsx';

import {
  SelectWithFilterProps,
  SetupInputProps,
} from './SelectWithFilter.interfaces';
import { DictionaryEntry } from 'app/services/dict.service';
import { useStyles } from './SelectWithFilter.styles';

export function useSelectWithFilter(props: SelectWithFilterProps) {
  const classes = useStyles(props);
  const {
    dictionary,
    label,
    disabled,
    placeholder,
    readOnly,
    onFocus,
    onBlur,
    onChange,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(960));
  const shouldSkipNextClickRef = useRef(false);
  const wasPointerDownRef = useRef(false);

  const getHasSublabels = useCallback(() => {
    return dictionary.some((entry) => !!entry.subLabels);
  }, [dictionary]);

  const onInputChange = useCallback(
    (
      inputValue: string | null,
      { setState, isOpen }: ControllerStateAndHelpers<SelectWithFilterProps>
    ) => {
      if (isOpen) {
        setState({ highlightedIndex: inputValue ? 0 : null });
      }
    },
    []
  );

  const getCurrentInputProps = useCallback(() => {
    return {
      label,
      disabled,
      placeholder,
      readOnly,
    };
  }, [label, disabled, placeholder, readOnly]);

  const getInputClasses = useCallback((hasSubLabels: boolean) => {
    return {
      classes: {
        root: classNames(
          {
            [classes.inputWithSubLabels]: hasSubLabels,
          },
          classes.inputRoot
        ),
        notchedOutline: classes.notchedOutline,
        focused: classes.focused,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectInputClick = useCallback(
    ({
      clearAndOpen,
      closeMenu,
      selectItem,
      currentSelectedItem,
      isOpen,
    }: SetupInputProps) => {
      if (isOpen) {
        closeMenu();
        selectItem(currentSelectedItem);
        return;
      }

      clearAndOpen();
    },
    []
  );

  const setupEventProps = useCallback(
    ({
      clearAndOpen,
      closeMenu,
      selectItem,
      currentSelectedItem,
      isOpen,
    }: SetupInputProps) => {
      return {
        onBlur: (e: FocusEvent) => {
          shouldSkipNextClickRef.current = false;
          wasPointerDownRef.current = false;
          onBlur && onBlur(e);
        },
        onMouseDown: (_e: MouseEvent<HTMLInputElement>) => {
          wasPointerDownRef.current = true;
        },
        onTouchStart: (_e: TouchEvent<HTMLInputElement>) => {
          wasPointerDownRef.current = true;
        },
        onClick: () =>
          shouldSkipNextClickRef.current
            ? (shouldSkipNextClickRef.current = false)
            : handleSelectInputClick({
                clearAndOpen,
                closeMenu,
                selectItem,
                currentSelectedItem,
                isOpen,
              }),
        onFocus: (e: FocusEvent) => {
          if (wasPointerDownRef.current) {
            shouldSkipNextClickRef.current = true;
            wasPointerDownRef.current = false;
          }
          if (onFocus) {
            onFocus(e);
          }
          clearAndOpen();
        },
        onKeyDown: (e: KeyboardEvent) => {
          const isArrowDownOrUpPressed =
            'ArrowUp' === e.key || 'ArrowDown' === e.key;
          if (!isOpen && isArrowDownOrUpPressed) {
            clearAndOpen();
          }
        },
      };
    },
    [handleSelectInputClick, onBlur, onFocus]
  );

  const getSelectedItem = (selectedKey: string | undefined) => {
    return dictionary.find(
      ({ key }: DictionaryEntry) => String(key) === String(selectedKey)
    );
  };

  const handleOnChange = useCallback(
    (dictEntry: DictionaryEntry) => {
      if (onChange && dictEntry) {
        onChange(dictEntry.key);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (dictionary.length === 1) {
      handleOnChange(dictionary[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionary]);

  return {
    isMobile,
    getSelectedItem,
    handleOnChange,
    onInputChange,
    getCurrentInputProps,
    getInputClasses,
    setupEventProps,
    getHasSublabels,
  };
}
